import { Box, Paper, Skeleton, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const cardSx = (radius: number) => ({
  p: 2.5,
  borderRadius: `${radius}px`,
  borderColor: 'divider',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.75,
})

/**
 * Loading skeleton mirroring the study screen. Which layout it mirrors depends
 * on what is about to arrive: Pro opens on the monitoring tab (KPI strip,
 * respondents table, activity), FaceWise opens straight into the results panel
 * (player + timeline, stats beside it). A skeleton of the wrong screen reads as
 * content that then vanishes.
 */
export default function StudyMonitoringSkeleton({
  variant = 'monitoring',
}: {
  variant?: 'monitoring' | 'results'
}) {
  const theme = useTheme()
  const r = theme.affexy.radii
  const results = variant === 'results'

  return (
    <Stack spacing={2.25} sx={{ maxWidth: 1340, mx: 'auto' }}>
      {/* Study header */}
      <Paper variant="outlined" sx={{ p: '22px', borderRadius: '18px', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={120} height={14} />
            <Skeleton variant="text" width={300} height={36} />
            <Skeleton variant="text" width={200} height={14} />
          </Box>
          <Stack direction="row" spacing={1}>
            {/* Conduct + Delete, plus Export where the edition has one. */}
            {(results ? [0, 1] : [0, 1, 2]).map((i) => (
              <Skeleton key={i} variant="rounded" width={96} height={36} />
            ))}
          </Stack>
        </Box>
        <Skeleton variant="rounded" height={48} sx={{ mt: 2 }} />
      </Paper>

      {/* Tab row — one view in the results layout, two tabs in monitoring. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', pb: 1 }}>
        <Stack direction="row" spacing={3}>
          {!results && (
            <>
              <Skeleton variant="text" width={90} height={20} />
              <Skeleton variant="text" width={90} height={20} />
            </>
          )}
        </Stack>
        <Skeleton variant="rounded" width={110} height={36} />
      </Box>

      {results ? (
        /* Results layout: player + timeline on the left, stats beside it. */
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.5fr 1fr' }, gap: 2.25, alignItems: 'start' }}>
          <Paper variant="outlined" sx={cardSx(r.lg / 1.33)}>
            <Skeleton variant="rounded" height={300} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={38} height={38} />
              <Skeleton variant="text" width={90} height={16} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="rounded" width={110} height={26} />
            </Box>
            <Skeleton variant="rounded" height={160} />
          </Paper>

          <Paper variant="outlined" sx={cardSx(r.lg / 1.33)}>
            <Skeleton variant="text" width={140} height={20} />
            {[0, 1, 2, 3, 4].map((j) => (
              <Skeleton key={j} variant="rounded" height={26} />
            ))}
          </Paper>
        </Box>
      ) : (
        <>
          {/* KPI strip */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 1.75 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Paper key={i} variant="outlined" sx={{ ...cardSx(r.md), minHeight: 110, gap: 1 }}>
                <Skeleton variant="text" width={70} height={12} />
                <Skeleton variant="text" width={50} height={30} />
              </Paper>
            ))}
          </Box>

          {/* Main grid: respondents table + activity/funnel */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.55fr 1fr' }, gap: 2.25 }}>
            <Paper variant="outlined" sx={cardSx(r.lg / 1.33)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Skeleton variant="text" width={160} height={20} />
                <Skeleton variant="rounded" width={110} height={32} />
              </Box>
              {[0, 1, 2, 3, 4, 5].map((j) => (
                <Skeleton key={j} variant="rounded" height={36} />
              ))}
            </Paper>

            <Stack spacing={2.25}>
              <Paper variant="outlined" sx={cardSx(r.lg / 1.33)}>
                <Skeleton variant="text" width={140} height={20} />
                {[0, 1, 2, 3].map((j) => (
                  <Box key={j} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="80%" height={14} />
                      <Skeleton variant="text" width="40%" height={12} />
                    </Box>
                  </Box>
                ))}
              </Paper>

              <Paper variant="outlined" sx={cardSx(r.lg / 1.33)}>
                <Skeleton variant="text" width={120} height={20} />
                {[0, 1, 2, 3].map((j) => (
                  <Skeleton key={j} variant="rounded" height={26} />
                ))}
              </Paper>
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  )
}
